import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import { TreeListComponent, TreeListDataResult } from '@progress/kendo-angular-treelist';
import { CompositeFilterDescriptor, FilterDescriptor, process, SortDescriptor, State } from '@progress/kendo-data-query';
import { catchError, EMPTY, finalize, from, map, mergeMap, Observable, Subject, switchMap, tap, toArray } from 'rxjs';
interface TreeListExpandState {
    expand(dataItem: unknown): void;
    isExpanded(dataItem: unknown): boolean;
}

import { App, EvaluatedFieldsProperties, FieldIdentifier, FieldStyleEvaluatedAction, FormulaMapping, Project, ReportDefinition, Site, SiteFormGridRowItem } from '../../models';
import { ReportViewGridColumnProperties } from '../../models/report-view-grid-column-properties';
import { ReportView } from '../../models/report-view-model';
import { LogicStyledGridRowCellDictionary } from '../../models/styled-grid-cell-column-mapping';
import { FORM_BUILDER_FORMAT_OPTIONS_CONSTANTS, SNACKBAR_CONSTANTS } from '../../shared/constants';
import { ClientErrorStatusCodes, ReportFilterType } from '../../shared/enums';
import { ProjectHelper } from '../../shared/helpers';
import { ReportsDataService, ReportsDefinitionDataService, SiteDataDataService } from '../data-services';
import { DateConversionService, naturalOrderBy } from '../utility-services';
import { ColorsUtilityService } from '../utility-services/colors-utility.service';
import { FormulaProperties } from './../../models/formula-properties';
import { EvaluatedLogicBuilder } from './../../models/logic-builder/evaluated-fields-properties';
import { FieldType, FieldTypeName } from './../../shared/enums/field-type.enum';
import { GridFieldTypes } from './enums/field-types';
import { DifferencesObject, Field, GridColumnProperties, ReportSiteData, ReportViewMatDialogData } from './models';
import { ReportGridDataSource } from './models/report-grid-data-source';
import { Report } from './models/report/report';
import { ReportDefinitionHeader } from './models/report/report-definition-header';
import { ReportFormattingData } from './models/report/report-formatting-data';
import { ReportsServiceModel } from './models/report/reports-service-model';

@Injectable({
    providedIn: 'root',
})
export class ReportsLogicService {
    private _reportDefinitionsHeadersChanged = new Subject<boolean>();
    private _reportDefinitionsHeadersFailedLoading = new Subject<number>();
    private _currentProjectFieldsSet = new Subject<void>();
    private _reportFormattingDataSet = new Subject<string>();
    private _reportViewCreated = new Subject<ReportView>();
    private _reportDataLoaded = new Subject<boolean>();
    private _reportIdLoaded = new Subject<string>();
    private _reportDataFailedLoading = new Subject<boolean>();
    private _dashboardReportDataFailedLoading = new Subject<string>();
    private _dashboardReportNotFoundError = new Subject<string>();
    private _reportRequest$ = new Subject<{ app: App; project: Project; reportDefinitionId: string; }>();
    private _dashboardAsyncReportLoadingLimit = 4;
    private _dashboardReportsInFlight = new Set<string>();

    model: ReportsServiceModel;
    currentProjectFieldsSet$ = this._currentProjectFieldsSet.asObservable();
    reportDefinitionsHeadersChanged$ = this._reportDefinitionsHeadersChanged.asObservable();
    reportDefinitionsHeadersFailedLoading$ = this._reportDefinitionsHeadersFailedLoading.asObservable();
    reportFormattingDataSet$ = this._reportFormattingDataSet.asObservable();
    reportViewCreated$ = this._reportViewCreated.asObservable();
    reportDataLoaded$ = this._reportDataLoaded.asObservable();
    reportIdLoaded$ = this._reportIdLoaded.asObservable();
    reportDataFailedLoading$ = this._reportDataFailedLoading.asObservable();
    dashboardReportDataFailedLoading$ = this._dashboardReportDataFailedLoading.asObservable();
    dashboardReportNotFoundError$ = this._dashboardReportNotFoundError.asObservable();

    pageSizes = [20, 50, 100];
    pageSize = this.pageSizes[1];
    reportDefinitionId = 'reportDefinitionId';

    defaultViewId = 'default';
    defaultView: ReportView = {
        kendoGridState: {
            skip: 0,
            take: this.pageSize,
            sort: [] as SortDescriptor[],
            filter: {
                logic: 'and',
                filters: [] as FilterDescriptor[]
            }
        },
        id: this.defaultViewId,
        name: 'Default',
        columnSettings: [],
        reportDefinitionId: '',
    };

    constructor(
        private _reportDataService: ReportsDataService,
        private _reportDefinitionDataService: ReportsDefinitionDataService,
        private _snackBar: MatSnackBar,
        private _colorsUtilityService: ColorsUtilityService,
        private _siteDataDataService: SiteDataDataService,
        private _dateConversionService: DateConversionService,
    ) {
        this._reportRequest$.pipe(
            switchMap(({ app, project, reportDefinitionId }) =>
                this.getFormattedReportObservable(app, project, reportDefinitionId).pipe(
                    catchError(() => {
                        this._snackBar.open(SNACKBAR_CONSTANTS.REPORT_FETCH_ERROR, SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                        this.model.reportLoading = false;
                        this._reportDataFailedLoading.next(true);
                        return EMPTY; // swallow error so the subject stays alive
                    })
                )
            )
        ).subscribe((report) => {
            this.convertSiteStatusToMultiSelect(report);
            this.model.reportsMap.set(report.reportDefinitionId, report);
            if (report.columnSettings?.length) {
                this.dedupeColumnSettings(report.columnSettings);
                this.model.reportFormatData.columnSettings = report.columnSettings;
            }

            if (report.evaluatedFieldsProperties) {
                this.mapEvaluatedLogicBuilders(report);
            }

            this.model.reportLoading = false;
            this.getCurrentReportFormattingData(report.reportDefinitionId);
            this._reportDataLoaded.next(true);
        });
    }

    initModel(): void {
        if (this.model === undefined) {
            this.model = new ReportsServiceModel();
            this.model.reportDefinitionsHeaders = new Array<ReportDefinitionHeader>();
            this.model.reportLoading = false;
            this.model.reportsMap = new Map<string, Report>();
            this.model.reportFormatData = new ReportFormattingData();
            this.model.reportFormatData.columnSettings = new Array<GridColumnProperties>();
            this.model.reportFormatData.hiddenColumns = new Map<string, boolean>();
            this.model.reportFormatDataMap = new Map<string, ReportFormattingData>();
        }
    }

    reInitModel(): void {
        this.model = new ReportsServiceModel();
        this.model.reportDefinitionsHeaders = new Array<ReportDefinitionHeader>();
        this.model.reportLoading = false;
        this.model.reportsMap = new Map<string, Report>();
        this.model.reportFormatData = new ReportFormattingData();
        this.model.reportFormatData.columnSettings = new Array<GridColumnProperties>();
        this.model.reportFormatData.hiddenColumns = new Map<string, boolean>();
        this.model.reportFormatDataMap = new Map<string, ReportFormattingData>();
    }

    getReportDefinitionsHeaders(app: App, project: Project): void {
        if (!project?.id || project?.id === '') {
            return;
        }

        const reportDefinitionsHeadersRequest$ = project.isSummaryProject === true
            ? this._reportDataService.getSummaryProjectReportDefinitionsHeaders(app, project)
            : this._reportDataService.getReportDefinitionsHeaders(app, project);

        reportDefinitionsHeadersRequest$
            .subscribe({
                next: (result: ReportDefinitionHeader[]) => {
                    this.model.reportDefinitionsHeaders = result;
                    this._reportDefinitionsHeadersChanged.next(true);
                },
                error: (error: HttpErrorResponse) => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.REPORT_FETCH_ERROR, SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                    this._reportDefinitionsHeadersFailedLoading.next(error?.status);
                },
            })
            .add();
    }

    getReportByReportDefinitionId(app: App, project: Project, reportDefinitionId: string): void {
        if (!this.model.reportsMap.has(reportDefinitionId)) {
            this.model.reportLoading = true;
            this._reportRequest$.next({ app, project, reportDefinitionId });
        }

        this.getCurrentReportFormattingData(reportDefinitionId);

        if (this.model.reportsMap.has(reportDefinitionId)) {
            this._reportDataLoaded.next(true);
        }
    }

    mapEvaluatedLogicBuilders(report: Report): void {
        const mappedEvaluatedLogicBuilders = new Map<string, EvaluatedFieldsProperties>();
        const returnedObject = report.evaluatedFieldsProperties;

        for (const key in report.evaluatedFieldsProperties) {
            const evaluatedLogicBuilder = new EvaluatedLogicBuilder();
            evaluatedLogicBuilder.fieldStyleEvaluatedActions = returnedObject[key].evaluatedLogicBuilder.fieldStyleEvaluatedActions;
            evaluatedLogicBuilder.mapPinColor = returnedObject[key].evaluatedLogicBuilder.mapPinColor;
            evaluatedLogicBuilder.siteData = returnedObject[key].evaluatedLogicBuilder.siteData;
            evaluatedLogicBuilder.fieldsSettableByLogicBuilders = returnedObject[key].evaluatedLogicBuilder.fieldsSettableByLogicBuilders;
            evaluatedLogicBuilder.triggerFields = returnedObject[key].evaluatedLogicBuilder.triggerFields;
            const formulaProperties = new FormulaProperties();
            formulaProperties.formulaOperands = returnedObject[key].formulaProperties.formulaOperands;
            formulaProperties.formulaMappings = returnedObject[key].formulaProperties.formulaMappings;
            const evaluatedFieldsProperties = new EvaluatedFieldsProperties();
            evaluatedFieldsProperties.evaluatedLogicBuilder = evaluatedLogicBuilder;
            evaluatedFieldsProperties.formulaProperties = formulaProperties;
            mappedEvaluatedLogicBuilders.set(key, evaluatedFieldsProperties);
        }
        report.evaluatedFieldsProperties = mappedEvaluatedLogicBuilders;
    }

    getCurrentReportFormattingData(reportDefinitionId: string): void {
        if (reportDefinitionId === undefined || reportDefinitionId === null) {
            return;
        }

        const report = this.model.reportsMap.get(reportDefinitionId);

        if (!report) {
            return;
        }

        const savedFormattingData = this.model.reportFormatDataMap.get(reportDefinitionId);

        if (savedFormattingData) {
            this.model.reportFormatData = savedFormattingData;
        }
        else {
            // Seed from the report so an already cached report - one a dashboard widget loaded - initializes
            // exactly as a freshly fetched one does, instead of inheriting the previous report's state.
            this.model.reportFormatData ??= new ReportFormattingData();

            if (report.columnSettings?.length) {
                this.model.reportFormatData.columnSettings = report.columnSettings;
            }

            this.model.reportFormatDataMap.set(reportDefinitionId, this.model.reportFormatData);
        }

        this.updateDefaultViewFromReport(report);
        this._reportFormattingDataSet.next(reportDefinitionId);
    }

    updateReportFormattingDataInSession(reportDefinitionHeader: ReportDefinitionHeader): void {
        if (this.model.reportFormatData.columnSettings.length || this.model.reportFormatData.filters.length || this.areAnyColumnsHidden() || this.areAnyColumnsLocked()) {
            this.model.reportFormatDataMap.set(reportDefinitionHeader.id, this.model.reportFormatData);
        }
    }

    areAnyColumnsHidden(): boolean {
        return Array.from(this.model.reportFormatData.hiddenColumns.values()).some((value: boolean) => value);
    }

    areAnyColumnsLocked(): boolean {
        return Array.from(this.model.reportFormatData.lockedColumns.values()).some((value: boolean) => value);
    }

    clearSavedReportData(reportDefinitionId?: string): void {
        const reportDefinitionIdToSave = reportDefinitionId ?? this.model.report?.reportDefinitionId;

        if (reportDefinitionIdToSave) {
            this.model.reportFormatDataMap.set(reportDefinitionIdToSave, this.model.reportFormatData);
        }

        this.model.reportFormatData = new ReportFormattingData();
    }

    clearReportsModel(): void {
        this.clearSavedReportData();
        this.model.reportDefinitionsHeaders = new Array<ReportDefinitionHeader>();
    }

    clearReportsOnDestroy(): void {
        if (this.model !== undefined) {
            this.model.reportDefinitionsHeaders = new Array<ReportDefinitionHeader>();
            this.model.reportsMap = new Map<string, Report>();
        }
    }

    setCurrentProjectFields(fields: Field[]): void {
        this.model.currentProjectFields = fields;
        this._currentProjectFieldsSet.next();
    }

    convertSiteStatusToMultiSelect(report: Report): void {
        if (!report?.selectedFields?.length) {
            return;
        }

        const statusField = report.selectedFields.find((field: Field) => field.id === '7');
        if (!statusField) {
            return;
        }

        statusField.FieldType.id = FieldType.DropDown;
        statusField.FieldType.text = 'DropDown';
        statusField.FieldType.type = 'string';
        statusField.FieldType.enum = ['Active', 'Inactive'];
        statusField.reportFilterType = ReportFilterType.MultiSelect;
    }

    isAutoSelectedField(field: Field, siteAlias: string): boolean {
        const autoSelectedFields = [`${siteAlias}No`, `${siteAlias}Name`, `${siteAlias}Status`];
        return autoSelectedFields.includes(field.serializedId ?? '');
    }

    isAutoSelectedIds(report: ReportDefinition | undefined, siteAlias: string): Set<string> {
        return new Set(
            report?.autoSelectedFields?.filter((field: Field) => this.isAutoSelectedField(field, siteAlias))
                .map((field: Field) => field.id)
                .filter((id): id is string => !!id) ?? []
        );
    }

    // A view only stores settings for columns that existed when it was saved, so a column is in the view unless explicitly hidden — the same rule the report grid applies.
    getFieldsVisibleInView(fields: Field[], view: ReportView | undefined): Field[] {
        const hiddenSettings = view?.columnSettings?.filter(setting => setting.hidden) ?? [];
        const hiddenSerializedIds = new Set(hiddenSettings.map(setting => setting.field));
        const hiddenIds = new Set(hiddenSettings.map(setting => setting.id).filter((id): id is string => !!id));

        return fields.filter(field => !hiddenIds.has(field.id) && !hiddenSerializedIds.has(field.serializedId ?? ''));
    }

    updateReportColumnWidth(app: App, project: Project, reportDefinitionHeader: ReportDefinitionHeader, columnSettings: GridColumnProperties): void {
        this._reportDefinitionDataService.updateReportColumnWidth(app, project, reportDefinitionHeader, columnSettings)
            .subscribe({
                next: (result: Report) => {
                    this.model.reportsMap.set(result.reportDefinitionId, result);
                    this.updateDefaultViewFromReport(result);
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.UPDATE_REPORT_ERROR, SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                },
            })
            .add(() => {
                this.model.reportLoading = false;
                this.getCurrentReportFormattingData(reportDefinitionHeader.id);
            });
    }

    updateReportColumnAggregator(app: App, project: Project, reportDefinitionHeader: ReportDefinitionHeader, columnSettings: GridColumnProperties): void {
        this._reportDefinitionDataService.updateReportColumnAggregator(app, project, reportDefinitionHeader, columnSettings)
            .subscribe({
                next: (result: Report) => {
                    this.model.reportsMap.set(result.reportDefinitionId, result);
                    this.updateDefaultViewFromReport(result);
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Report Aggregation', 'updating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                },
            })
            .add(() => {
                this.model.reportLoading = false;
                this.getCurrentReportFormattingData(reportDefinitionHeader.id);
            });
    }

    updateReportViewColumnWidth(app: App, project: Project, reportView: ReportView, columnChanged: GridColumnProperties): void {
        this.setColumnWidth(reportView.columnSettings, columnChanged.field, columnChanged.width);

        this._reportDefinitionDataService.updateReportView(app, project, reportView)
            .subscribe({
                next: (result: ReportView) => {
                    const existingReport = this.model.reportsMap.get(result.reportDefinitionId);
                    if (!existingReport.customViews) {
                        existingReport.customViews = [];
                    }

                    const existingIndex = existingReport.customViews.findIndex(view => view.id === result.id);
                    if (existingIndex !== -1) {
                        existingReport.customViews[existingIndex] = result;
                    }
                }
            });
    }

    private hasUsableWidth(width: number): boolean {
        return width > 0;
    }

    applyExistingColumnSettings(columnId: string, fieldDefaultWidth: number, columnSettings: GridColumnProperties[]): number {
        const existingWidth = columnSettings?.find((setting: GridColumnProperties) => setting.field === columnId)?.width;

        return this.hasUsableWidth(existingWidth) ? existingWidth : fieldDefaultWidth;
    }

    setColumnWidth(columnSettings: GridColumnProperties[], field: string, width: number): GridColumnProperties {
        const existingSetting = columnSettings.find((setting: GridColumnProperties) => setting.field === field);

        if (existingSetting) {
            existingSetting.width = width;
            return existingSetting;
        }

        const newSetting: GridColumnProperties = { field, width };
        columnSettings.push(newSetting);

        return newSetting;
    }

    dedupeColumnSettings(columnSettings: GridColumnProperties[]): void {
        if (!columnSettings?.length) {
            return;
        }

        const settingsByField = new Map<string, GridColumnProperties>();

        columnSettings.forEach((setting: GridColumnProperties) => {
            const existingSetting = settingsByField.get(setting.field);

            if (!existingSetting) {
                settingsByField.set(setting.field, setting);
            }
            else if (!this.hasUsableWidth(existingSetting.width)) {
                existingSetting.width = setting.width;
            }
        });

        if (settingsByField.size < columnSettings.length) {
            columnSettings.splice(0, columnSettings.length, ...settingsByField.values());
        }
    }

    getWidthBasedOnFieldType(fieldType: string): number {
        switch (fieldType) {
            case FieldType.CheckBox:
            case FieldType.Number:
            case FieldType.DateSelect:
                return 140;
            case FieldType.DropDown:
            case FieldType.TextBox:
            case FieldType.Person:
            case GridFieldTypes.Text:
            case 'Textbox':
                return 200;
            case FieldType.TextArea:
                return 350;
            case FieldType.Uploader:
            case FieldType.EmailHyperlink:
            case FieldType.URLHyperlink:
                return 250;
            default:
                return 250;
        }
    }

    createReportView(app: App, project: Project, report: Report, reportView: ReportView): void {
        this._reportDefinitionDataService.createNewReportDefinitionView(app, project, report, reportView)
            .subscribe({
                next: (result: ReportView) => {
                    const existingReport = this.model.reportsMap.get(result.reportDefinitionId);
                    if (!existingReport.customViews) {
                        existingReport.customViews = [];
                    }
                    existingReport.customViews = [...existingReport.customViews, result];
                    this._reportViewCreated.next(result);

                    this._snackBar.open(SNACKBAR_CONSTANTS.REPORT_VIEW_ADDED(result.name), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.REPORT_VIEW_ADD_ERROR, SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                }
            });
    }

    updateReportView(app: App, project: Project, reportView: ReportView): void {
        this._reportDefinitionDataService.updateReportView(app, project, reportView)
            .subscribe({
                next: (result: ReportView) => {
                    const existingReport = this.model.reportsMap.get(result.reportDefinitionId);
                    const existingIndex = existingReport.customViews.findIndex((view: ReportView) => view.id === result.id);
                    if (existingIndex !== -1) {
                        existingReport.customViews = existingReport.customViews.map(
                            (view: ReportView) => view.id === result.id ? result : view
                        );
                    }

                    this._snackBar.open(SNACKBAR_CONSTANTS.REPORT_VIEW_UPDATED(result.name), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.REPORT_VIEW_UPDATE_ERROR(reportView.name), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                }
            });
    }

    deleteReportView(app: App, project: Project, reportView: ReportView): void {
        this._reportDefinitionDataService.deleteReportView(app, project, reportView)
            .subscribe({
                next: (result: ReportView[]) => {
                    const existingReport = this.model.reportsMap.get(reportView.reportDefinitionId);

                    existingReport.customViews = result;
                    this._snackBar.open(SNACKBAR_CONSTANTS.REPORT_VIEW_DELETED(reportView.name), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.REPORT_VIEW_DELETE_ERROR(reportView.name), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                }
            });
    }

    patchLogicBuilderTriggerField(currentApp: App, currentProject: Project, currentSite: Site, report: Report, fieldToUpdate: DifferencesObject): void {
        this._siteDataDataService.patchLogicBuilderTriggerField(currentApp, currentProject, currentSite, fieldToUpdate)
            .subscribe({
                next: (response: EvaluatedFieldsProperties) => {
                    this.setReportEvaluatedLogicBuilder(currentSite, report, response.evaluatedLogicBuilder);
                    this._snackBar.dismiss();
                    this._snackBar.open(SNACKBAR_CONSTANTS.SITE_DATA_SAVED(currentApp.aliases.site.singular), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.SAVE_ERROR, SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });

                    this._reportDataFailedLoading.next(true);
                },
                complete: () => {
                    this._reportDataLoaded.next(true);
                }
            });
    }

    patchField(currentApp: App, currentProject: Project, currentSite: Site, fieldToUpdate: DifferencesObject): void {
        this._siteDataDataService.patchField(currentApp, currentProject, currentSite, fieldToUpdate)
            .subscribe({
                next: () => {
                    this._snackBar.dismiss();
                    this._snackBar.open(SNACKBAR_CONSTANTS.SITE_DATA_SAVED(currentApp.aliases.site.singular), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.SAVE_ERROR, SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });

                    this._reportDataFailedLoading.next(true);
                },
                complete: () => {
                    this._reportDataLoaded.next(true);
                }
            });
    }

    patchFormulaOperandField(currentApp: App, currentProject: Project, currentSite: Site, report: Report, fieldToUpdate: DifferencesObject): void {
        this._siteDataDataService.patchReportsFormulaOperandField(currentApp, currentProject, currentSite, fieldToUpdate)
            .subscribe({
                next: (response: EvaluatedFieldsProperties) => {
                    this.setReportEvaluatedLogicBuilder(currentSite, report, response.evaluatedLogicBuilder);
                    this.updateSiteReportFormulaFields(currentSite, report);
                    this._snackBar.dismiss();
                    this._snackBar.open(SNACKBAR_CONSTANTS.SITE_DATA_SAVED(currentApp.aliases.site.singular), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.SAVE_ERROR, SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });

                    this._reportDataFailedLoading.next(true);
                },
                complete: () => {
                    this._reportDataLoaded.next(true);
                }
            });
    }

    removeLogicBuilderTriggerField(currentApp: App, currentProject: Project, currentSite: Site, report: Report, fieldToUpdate: DifferencesObject): void {
        this._siteDataDataService.removeLogicBuilderTriggerField(currentApp, currentProject, currentSite, fieldToUpdate)
            .subscribe({
                next: (response: EvaluatedFieldsProperties) => {
                    this.setReportEvaluatedLogicBuilder(currentSite, report, response.evaluatedLogicBuilder);
                    this._snackBar.dismiss();
                    this._snackBar.open(SNACKBAR_CONSTANTS.SITE_DATA_SAVED(currentApp.aliases.site.singular), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.SAVE_ERROR, SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });

                    this._reportDataFailedLoading.next(true);
                },
                complete: () => {
                    this._reportDataLoaded.next(true);
                }
            });
    }

    removeField(currentApp: App, currentProject: Project, currentSite: Site, fieldToUpdate: DifferencesObject): void {
        this._siteDataDataService.removeField(currentApp, currentProject, currentSite, fieldToUpdate)
            .subscribe({
                next: () => {
                    this._snackBar.dismiss();
                    this._snackBar.open(SNACKBAR_CONSTANTS.SITE_DATA_SAVED(currentApp.aliases.site.singular), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.SAVE_ERROR, SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });

                    this._reportDataFailedLoading.next(true);
                },
                complete: () => {
                    this._reportDataLoaded.next(true);
                }
            });
    }

    removeFormulaOperandField(currentApp: App, currentProject: Project, currentSite: Site, report: Report, fieldToUpdate: DifferencesObject): void {
        this._siteDataDataService.removeReportsFormulaOperandField(currentApp, currentProject, currentSite, fieldToUpdate)
            .subscribe({
                next: (response: EvaluatedFieldsProperties) => {
                    this.setReportEvaluatedLogicBuilder(currentSite, report, response.evaluatedLogicBuilder);
                    this._snackBar.dismiss();
                    this._snackBar.open(SNACKBAR_CONSTANTS.SITE_DATA_SAVED(currentApp.aliases.site.singular), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.SAVE_ERROR, SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });

                    this._reportDataFailedLoading.next(true);
                },
                complete: () => {
                    this._reportDataLoaded.next(true);
                }
            });
    }

    addGridColumnProperties(wrappedColumns: Map<string, boolean>, reportFormattingData: ReportFormattingData, report: Report): ReportViewGridColumnProperties[] {
        const combinedGridSettings: ReportViewGridColumnProperties[] = [];

        report.selectedFields?.forEach((reportField: Field) => {
            const defaultWidth = this.getWidthBasedOnFieldType(reportField.FieldType?.id);
            const combinedProperties: ReportViewGridColumnProperties = {
                field: reportField.serializedId,
                width: this.applyExistingColumnSettings(reportField.serializedId, defaultWidth, reportFormattingData.columnSettings),
                locked: reportFormattingData.lockedColumns.get(reportField.serializedId),
                hidden: reportFormattingData.hiddenColumns.get(reportField.serializedId),
                shouldWrap: wrappedColumns.get(reportField.id),
                id: reportField.id
            };
            combinedGridSettings.push(combinedProperties);
        });

        return combinedGridSettings;
    }

    convertWrappedColumnsToMap(selectedView: ReportView, wrappedColumns: Map<string, boolean>): void {
        if (!wrappedColumns) {
            wrappedColumns = new Map<string, boolean>();
        }

        if (!selectedView || selectedView.id == this.defaultViewId) {

            wrappedColumns?.forEach((value, key) => {
                wrappedColumns.set(key, value = false);
            });
        }
        else {
            selectedView.columnSettings.forEach((column: ReportViewGridColumnProperties) => {
                wrappedColumns.set(column.id ?? '', column?.shouldWrap ?? false);
            });
        }
    }

    updateDefaultViewFromReport(report: Report): void {
        this.defaultView.columnSettings = [];
        report?.columnSettings?.forEach((columnProperties: GridColumnProperties) => {
            const columnPropertiesToAdd: ReportViewGridColumnProperties = {
                field: columnProperties.field,
                width: columnProperties.width,
                locked: false,
                hidden: false
            };
            this.defaultView.columnSettings.push(columnPropertiesToAdd);
        });
    }

    validateIfViewExist(report: Report, viewNameData: ReportViewMatDialogData): boolean {
        const result = report.customViews?.some((view: ReportView) => view.name.toLocaleLowerCase() === viewNameData.viewName.toLocaleLowerCase());
        if (result) {
            this._snackBar.open(SNACKBAR_CONSTANTS.REPORT_VIEW_ALREADY_EXISTS(viewNameData.viewName), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
        }

        return result;
    }

    validateViewProjectAdmin(isProjectAdmin: boolean, adminProjects: Project[], currentProject: Project): boolean {
        if (!isProjectAdmin) {
            return false;
        }

        return adminProjects.some(project => project.id === currentProject.id);
    }

    getGridCellStyleClasses(report: Report, logicStyleRowCellMapping: Map<string, LogicStyledGridRowCellDictionary>): void {
        const gridInReport = report.selectedFields?.filter((field: Field) => field.dataId)[0]?.dataId;

        report.reportData?.forEach((reportRow: ReportSiteData) => {
            if (!report.evaluatedFieldsProperties.has(reportRow.SiteId)) {
                return;
            }

            const evaluatedLogicBuilderStyleActions = report.evaluatedFieldsProperties.get(reportRow.SiteId).evaluatedLogicBuilder.fieldStyleEvaluatedActions;
            if (!evaluatedLogicBuilderStyleActions?.length && !this.areAnyReportSelectedFieldsActionStyleFields(report, evaluatedLogicBuilderStyleActions)) {
                this.clearAnyExistingStylesForRow(reportRow, logicStyleRowCellMapping, gridInReport);
                return;
            }

            this.evaluateEachReportRowColumnForStyle(reportRow, evaluatedLogicBuilderStyleActions, logicStyleRowCellMapping, gridInReport);
        });
    }

    evaluateEachReportRowColumnForStyle(reportRow: ReportSiteData, evaluatedLogicBuilderStyleActions: FieldStyleEvaluatedAction[], logicStyleRowCellMapping: Map<string, LogicStyledGridRowCellDictionary>, gridInReport: string): void {
        if (logicStyleRowCellMapping.has(reportRow.id)) {
            this.clearAnyExistingStylesForRow(reportRow, logicStyleRowCellMapping, gridInReport);
        }
        for (const key in reportRow) {
            const actualFieldId = key.replace('$', '').replace(/_/g, '-');
            const actionsOnField = evaluatedLogicBuilderStyleActions.filter((action: FieldStyleEvaluatedAction) => action.fieldId === actualFieldId || action.gridColumnId === actualFieldId);

            actionsOnField.forEach((action: FieldStyleEvaluatedAction) => {
                if (this.shouldApplyStylingToFieldCell(action, actualFieldId)) {
                    this.setStylingClassToFieldCell(logicStyleRowCellMapping, reportRow, action);
                    return;
                }
                if (this.shouldApplyStylingToGridFieldCell(action, actualFieldId, gridInReport, reportRow)) {
                    this.setStylingClassToGridFieldCell(logicStyleRowCellMapping, reportRow, action);
                }
            });
        }
    }

    clearAnyExistingStylesForRow(reportRow: ReportSiteData, logicStyleRowCellMapping: Map<string, LogicStyledGridRowCellDictionary>, gridInReport: string): void {
        if (logicStyleRowCellMapping.has(reportRow.id)) {
            logicStyleRowCellMapping.delete(reportRow.id);
        }
    }

    shouldApplyStylingToFieldCell(action: FieldStyleEvaluatedAction, actualFieldId: string): boolean {
        return action.fieldId === actualFieldId;
    }

    shouldApplyStylingToGridFieldCell(action: FieldStyleEvaluatedAction, actualFieldId: string, gridInReport: string, reportRow: ReportSiteData): boolean {
        return action.gridColumnId === actualFieldId && action.fieldId === gridInReport && reportRow.gridItemId === action.gridRowId;
    }

    setStylingClassToFieldCell(logicStyleRowCellMapping: Map<string, LogicStyledGridRowCellDictionary>, reportRow: ReportSiteData, action: FieldStyleEvaluatedAction): void {
        if (!logicStyleRowCellMapping.has(reportRow.id)) {
            logicStyleRowCellMapping.set(reportRow.id, {} as LogicStyledGridRowCellDictionary);
        }

        logicStyleRowCellMapping.get(reportRow.id)[this.serializeId(action.fieldId)] = this._colorsUtilityService.getFieldStyleColorClassByColor(action.colorId);
    }

    setStylingClassToGridFieldCell(logicStyleRowCellMapping: Map<string, LogicStyledGridRowCellDictionary>, reportRow: ReportSiteData, action: FieldStyleEvaluatedAction): void {
        if (!logicStyleRowCellMapping.has(reportRow.id)) {
            logicStyleRowCellMapping.set(reportRow.id, {} as LogicStyledGridRowCellDictionary);
        }

        logicStyleRowCellMapping.get(reportRow.id)[this.serializeId(action.gridColumnId)] = this._colorsUtilityService.getFieldStyleColorClassByColor(action.colorId);
    }

    serializeId(fieldId: string): string {
        return `$${fieldId.replace(/-/g, '_')}`;
    }

    setReportEvaluatedLogicBuilder(currentSite: Site, report: Report, updatedEvaluatedLogicBuilder: EvaluatedLogicBuilder,): void {
        report.evaluatedFieldsProperties.get(currentSite.id).evaluatedLogicBuilder.fieldStyleEvaluatedActions = updatedEvaluatedLogicBuilder.fieldStyleEvaluatedActions;
        report.evaluatedFieldsProperties.get(currentSite.id).evaluatedLogicBuilder.siteData = updatedEvaluatedLogicBuilder.siteData;

        this.updateSiteReportSettableFields(currentSite, report);
        this.updateSiteReportFormulaFields(currentSite, report);
    }

    updateSiteReportSettableFields(currentSite: Site, report: Report): void {
        const siteRows = report.reportData.filter((reportRow: ReportSiteData) => reportRow.SiteId === currentSite.id);

        if (!report.evaluatedFieldsProperties.has(currentSite.id)) {
            return;
        }

        const siteEvaluatedLogicBuilder = report.evaluatedFieldsProperties.get(currentSite.id).evaluatedLogicBuilder;

        const evaluatedSettableFields = report.evaluatedFieldsProperties.get(currentSite.id).evaluatedLogicBuilder.fieldsSettableByLogicBuilders;

        if (!evaluatedSettableFields?.length) {
            return;
        }

        if (!this.areAnyReportSelectedFieldsSettable(report, evaluatedSettableFields)) {
            return;
        }

        const settableFieldsInReport = evaluatedSettableFields.filter((evaluatedSetField: FieldIdentifier) => {
            return report.selectedFields.some((selectedField: Field) => {
                return selectedField.id === evaluatedSetField.fieldId || selectedField.id === evaluatedSetField.gridColumnId;
            });
        });

        siteRows.forEach((reportRow: ReportSiteData) => {
            for (const key in reportRow) {
                const actualFieldId = key.replace('$', '').replace(/_/g, '-');
                if (!settableFieldsInReport.some((settableField: FieldIdentifier) => settableField.fieldId == actualFieldId || settableField.gridColumnId == actualFieldId)) {
                    continue;
                }

                const fieldToUpdate = settableFieldsInReport.find((field: FieldIdentifier) => field.fieldId === actualFieldId || field.gridColumnId === actualFieldId);
                if (!fieldToUpdate?.gridColumnId) {
                    this.updateReportDataSettableField(reportRow, key, fieldToUpdate, siteEvaluatedLogicBuilder);
                    continue;
                }

                const gridIdInReport = report.selectedFields.filter((field: Field) => field.dataId)[0]?.dataId;

                this.updateReportDataSettableGridColumn(reportRow, key, fieldToUpdate, siteEvaluatedLogicBuilder, gridIdInReport);
            }
        });
    }

    updateReportDataSettableField(reportRow: ReportSiteData, key: string, fieldToUpdate: FieldIdentifier, siteEvaluatedLogicBuilder: EvaluatedLogicBuilder): void {
        reportRow[key] = siteEvaluatedLogicBuilder.siteData[fieldToUpdate.fieldId];
    }

    updateReportDataSettableGridColumn(
        reportRow: ReportSiteData,
        key: string,
        fieldToUpdate: FieldIdentifier,
        siteEvaluatedLogicBuilder: EvaluatedLogicBuilder,
        gridIdInReport: string): void {
        const gridData = siteEvaluatedLogicBuilder.siteData[gridIdInReport];
        if (!Array.isArray(gridData) || !gridData.length) {
            return;
        }

        const sourceGridRow = gridData.find((row: SiteFormGridRowItem) => row.id === reportRow.gridItemId);

        if (sourceGridRow) {
            reportRow[key] = sourceGridRow[fieldToUpdate.gridColumnId];
        }
    }

    areAnyReportSelectedFieldsSettable(report: Report, evaluatedSettableFields: FieldIdentifier[]): boolean {
        return report.selectedFields.some((selectedField: Field) => {
            return evaluatedSettableFields.some((evaluatedSettableField: FieldIdentifier) => {
                return selectedField.id === evaluatedSettableField.fieldId || selectedField.id === evaluatedSettableField.gridColumnId;
            });
        });
    }

    updateSiteReportFormulaFields(currentSite: Site, report: Report): void {
        const siteRows = report.reportData.filter((reportRow: ReportSiteData) => reportRow.SiteId === currentSite.id);

        if (!report.evaluatedFieldsProperties.has(currentSite.id)) {
            return;
        }

        const siteEvaluatedLogicBuilder = report.evaluatedFieldsProperties.get(currentSite.id).evaluatedLogicBuilder;

        if (!this.areAnyReportSelectedFieldsFormulaFields(report)) {
            return;
        }

        const formulaFieldsInReport = report.selectedFields.filter((selectedField: Field) => {
            return selectedField.FieldType.id === FieldType.Formula;
        });

        siteRows.forEach((reportRow: ReportSiteData) => {
            for (const key in reportRow) {
                const actualFieldId = key.replace('$', '').replace(/_/g, '-');
                if (!formulaFieldsInReport.some((formulaField: Field) => formulaField.id == actualFieldId)) {
                    continue;
                }

                const fieldToUpdate = formulaFieldsInReport.find((field: Field) => field.id === actualFieldId);
                if (!fieldToUpdate?.dataId) {
                    this.updateReportDataFormulaField(reportRow, key, fieldToUpdate, siteEvaluatedLogicBuilder);
                    continue;
                }

                const gridIdInReport = report.selectedFields.filter((field: Field) => field.dataId)[0]?.dataId;

                this.updateReportDataFormulaGridColumn(reportRow, key, fieldToUpdate, siteEvaluatedLogicBuilder, gridIdInReport);
            }
        });
    }

    areAnyReportSelectedFieldsFormulaFields(report: Report): boolean {
        return report.selectedFields.some((selectedField: Field) => selectedField.FieldType.id === FieldType.Formula);
    }

    updateReportDataFormulaField(reportRow: ReportSiteData, key: string, fieldToUpdate: Field, siteEvaluatedLogicBuilder: EvaluatedLogicBuilder): void {
        reportRow[key] = siteEvaluatedLogicBuilder.siteData[fieldToUpdate.id];
    }

    updateReportDataFormulaGridColumn(reportRow: ReportSiteData, key: string, fieldToUpdate: Field, siteEvaluatedLogicBuilder: EvaluatedLogicBuilder, gridIdInReport: string): void {
        const gridData = siteEvaluatedLogicBuilder.siteData[gridIdInReport];
        if (!Array.isArray(gridData) || !gridData.length) {
            return;
        }

        const sourceGridRow = gridData.find((row: SiteFormGridRowItem) => row.id === reportRow.gridItemId);

        if (sourceGridRow) {
            reportRow[key] = sourceGridRow[fieldToUpdate.id];
        }
    }

    areAnyReportSelectedFieldsActionStyleFields(report: Report, actionStyleFields: FieldStyleEvaluatedAction[]): boolean {
        return report.selectedFields.some((selectedField: Field) => {
            return actionStyleFields.some((fieldToApplyStyle: FieldStyleEvaluatedAction) => {
                return selectedField.id === fieldToApplyStyle.fieldId || selectedField.id === fieldToApplyStyle.gridColumnId;
            });
        });
    }

    isFieldToUpdateATriggerField(currentApp: App, report: Report, fieldToUpdate: DifferencesObject): boolean {
        if (!currentApp.showLogicBuilder) {
            return false;
        }

        if (!report.evaluatedFieldsProperties?.has(fieldToUpdate.siteId)) {
            return false;
        }

        if (report.evaluatedFieldsProperties?.get(fieldToUpdate.siteId).evaluatedLogicBuilder.triggerFields?.some((triggerField: FieldIdentifier) => triggerField.fieldId === fieldToUpdate.fieldId || triggerField.gridColumnId === fieldToUpdate.fieldId)) {
            return true;
        }

        return false;
    }

    isFieldASettableField(currentApp: App, report: Report, fieldToUpdate: Field, siteId: string): boolean {
        if (!currentApp.showLogicBuilder) {
            return false;
        }

        if (!report.evaluatedFieldsProperties?.has(siteId)) {
            return false;
        }

        if (report.evaluatedFieldsProperties?.get(siteId).evaluatedLogicBuilder.fieldsSettableByLogicBuilders?.some((settableField: FieldIdentifier) => settableField.fieldId === fieldToUpdate.id || settableField.gridColumnId === fieldToUpdate.id)) {
            return true;
        }

        return false;
    }

    isFieldAFormulaField(fieldTypeId: string): boolean {
        return fieldTypeId === FieldType.Formula;
    }

    isFieldToUpdateAFormulaOperandField(report: Report, fieldToUpdate: DifferencesObject): boolean {
        if (!report.evaluatedFieldsProperties?.has(fieldToUpdate.siteId)) {
            return false;
        }

        if (report.evaluatedFieldsProperties?.get(fieldToUpdate.siteId).formulaProperties.formulaOperands?.some((operandField: FieldIdentifier) => operandField.fieldId === fieldToUpdate.fieldId || operandField.gridColumnId === fieldToUpdate.fieldId)) {
            return true;
        }

        return false;
    }

    isFormulaForOperandATriggerField(report: Report, formulaOperandField: DifferencesObject): boolean {
        const triggerFields = report.evaluatedFieldsProperties?.get(formulaOperandField.siteId).evaluatedLogicBuilder.triggerFields;
        const formulaMappings = report.evaluatedFieldsProperties?.get(formulaOperandField.siteId).formulaProperties.formulaMappings;
        const formulasWithOperand = formulaMappings.filter((mapping: FormulaMapping) =>
            mapping.operands.some((operand: FieldIdentifier) =>
                operand.fieldId === formulaOperandField.fieldId && operand.gridColumnId === formulaOperandField.objectFieldId));

        if (formulasWithOperand.length === 0) {
            return false;
        }

        return formulasWithOperand.some((mapping: FormulaMapping) =>
            triggerFields.some((triggerField: FieldIdentifier) =>
                triggerField.fieldId === mapping.formula.fieldId &&
                triggerField.gridColumnId === mapping.formula.gridColumnId));
    }

    getGridStyleHexesFromClassesMap(logicStyleRowCellMapping: Map<string, LogicStyledGridRowCellDictionary>): Map<string, LogicStyledGridRowCellDictionary> {
        const hexesMap = new Map<string, LogicStyledGridRowCellDictionary>();
        logicStyleRowCellMapping.forEach((logicClassesMap: LogicStyledGridRowCellDictionary, rowId: string) => {
            const hexesDictionary: LogicStyledGridRowCellDictionary = {} as LogicStyledGridRowCellDictionary;
            for (const key in logicClassesMap) {
                const colorClass = logicClassesMap[key];
                if (colorClass) {
                    const hexColor = this._colorsUtilityService.getHexColorFromFieldStyleColorClass(colorClass);
                    hexesDictionary[key] = hexColor;
                }
            }
            hexesMap.set(rowId, hexesDictionary);
        });
        return hexesMap;
    }

    updateCellMappingHexesFromCurrentFilterSortState(filteredExcelData: any[], logicStyleRowCellMappingHexes: Map<string, LogicStyledGridRowCellDictionary>): Map<string, LogicStyledGridRowCellDictionary> {
        const rowIdsInFilteredData = new Set(filteredExcelData.map((row: any) => row.id));
        for (const key of logicStyleRowCellMappingHexes.keys()) {
            if (!rowIdsInFilteredData.has(key)) {
                logicStyleRowCellMappingHexes.delete(key);
            }
        }

        let rowIndex = 0;
        const rowIndexedHexesMap = new Map<string, LogicStyledGridRowCellDictionary>();
        filteredExcelData.forEach((row: any) => {
            if (logicStyleRowCellMappingHexes.has(row.id)) {
                rowIndexedHexesMap.set(rowIndex.toString(), logicStyleRowCellMappingHexes.get(row.id));
            }
            rowIndex++;
        });

        return rowIndexedHexesMap;
    }

    doesReportDefinitionContainGridsAndSubrows(reportDefinition: ReportDefinitionHeader, fields: Field[]): boolean {
        if (!reportDefinition?.enableSubRow) {
            return false;
        }
        const columnInGridAsField = reportDefinition.selectedFields.find((field: Field) => field.dataId);
        return !!columnInGridAsField && fields.some((field: Field) => field.id === columnInGridAsField.dataId && field.FieldType.id === FieldType.Grid && field.FieldType.enableSubRow);
    }

    getFormattedReportObservable(app: App, project: Project, reportDefinitionId: string): Observable<Report> {
        const reportRequest$ = project.isSummaryProject === true
            ? this._reportDataService.getSummaryProjectReportByReportDefinitionId(app, project, reportDefinitionId)
            : this._reportDataService.getReportByReportDefinitionId(app, project, reportDefinitionId);

        return reportRequest$
            .pipe(map((report: Report) => {
                report.selectedFields = ProjectHelper.updateOptionsFromProject(project, report.selectedFields);
                report.reportData = naturalOrderBy(report.reportData, [{ field: report.selectedFields[0]?.serializedId, dir: 'asc' } as SortDescriptor]);
                return report;
            }));
    }

    getFormattedActiveReportObservable(app: App, project: Project, reportDefinitionId: string): Observable<Report> {
        const activeReportRequest$ = project.isSummaryProject === true
            ? this._reportDataService.getSummaryProjectActiveReportByReportDefinitionId(app, project, reportDefinitionId)
            : this._reportDataService.getActiveReportByReportDefinitionId(app, project, reportDefinitionId);

        return activeReportRequest$
            .pipe(map((report: Report) => {
                report.selectedFields = ProjectHelper.updateOptionsFromProject(project, report.selectedFields ?? []);
                report.reportData = naturalOrderBy(report.reportData ?? [], [{ field: report.selectedFields[0]?.serializedId, dir: 'asc' } as SortDescriptor]);
                return report;
            }));
    }

    getDashboardReports(app: App, project: Project, reportIds: Set<string>): void {
        if (!reportIds?.size) {
            return;
        }

        const reportIdsToFetch = [...reportIds].filter(reportId =>
            !!reportId &&
            !this.model.reportsMap.has(reportId) &&
            !this._dashboardReportsInFlight.has(reportId)
        );
        if (!reportIdsToFetch.length) {
            return;
        }

        let hasFetchError = false;
        this.model.reportLoading = true;
        reportIdsToFetch.forEach(reportId => this._dashboardReportsInFlight.add(reportId));

        from(reportIdsToFetch)
            .pipe(
                mergeMap((reportDefinitionId) =>
                    this.getFormattedActiveReportObservable(app, project, reportDefinitionId).pipe(
                        tap((report: Report) => {
                            this.convertSiteStatusToMultiSelect(report);
                            if (report.columnSettings?.length) {
                                this.dedupeColumnSettings(report.columnSettings);
                                this.model.reportFormatData.columnSettings = report.columnSettings;
                            }

                            if (report!.evaluatedFieldsProperties) {
                                this.mapEvaluatedLogicBuilders(report);
                            }

                            if (report.reportDefinitionId) {
                                this.model.reportsMap.set(report!.reportDefinitionId, report);
                                this._reportIdLoaded.next(report!.reportDefinitionId);
                            }
                        }),
                        catchError((error) => {
                            hasFetchError = true;

                            if (error.status === ClientErrorStatusCodes.NotFound) {
                                this._dashboardReportNotFoundError.next(reportDefinitionId);
                            }
                            else {
                                this._dashboardReportDataFailedLoading.next(reportDefinitionId);
                            }

                            return EMPTY;
                        }),
                        finalize(() => {
                            this._dashboardReportsInFlight.delete(reportDefinitionId);
                        })
                    ),
                    this._dashboardAsyncReportLoadingLimit
                ),
                toArray(),
                finalize(() => {
                    this.model.reportLoading = false;
                    if (hasFetchError) {
                        this._snackBar.open(SNACKBAR_CONSTANTS.REPORT_FETCH_ERROR, SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                    }
                })
            )
            .subscribe();
    }

    getDistinctValues(report: Report, serializedId: string): any[] {
        const distinctValues = [...new Set(report?.reportData
            ?.map(reportData => reportData[serializedId]))]
            .filter(value => value !== "")
            .sort((a, b) => (a > b) ? 1 : -1);
        return distinctValues;
    }

    wrapRichTextColumn(event: any, column: Field, wrappedColumns: Map<string, boolean>): void {
        event.stopPropagation();
        wrappedColumns.set(column.id, !wrappedColumns.get(column.id));
    }

    initializeWrapForColumns(report: Report, wrappedColumns: Map<string, boolean>): void {
        report?.selectedFields?.forEach((selectedField: Field) => {
            wrappedColumns.set(selectedField.id, false);
        });
    }

    processReportDataForExport(reportData: ReportSiteData[], state: State, enableSubRow: boolean): { childData: ReportSiteData[]; parentData: ReportSiteData[]; } {
        const stateWithoutPagingAndSorting = this.prepareStateWithoutSorting(this.prepareStateWithoutPaging(state));

        const sortedData = naturalOrderBy(reportData ?? [], stateWithoutPagingAndSorting.sort || []);

        const allChildren = sortedData.filter(item => item.parentId);
        const allParents = sortedData.filter(item => !item.parentId);

        if (!this.hasFiltersInState(stateWithoutPagingAndSorting)) {
            return {
                childData: allChildren,
                parentData: allParents
            };
        }

        const allFiltered = process(sortedData, { filter: stateWithoutPagingAndSorting.filter }).data;
        const filteredChildren = allFiltered.filter(item => item.parentId);
        const filteredParentIds = new Set(allFiltered.filter(item => !item.parentId).map(item => item.id));

        let parentIds = filteredParentIds;
        if (enableSubRow) {
            const childParentIds = new Set(filteredChildren.map(child => child.indexedParentId).filter((id): id is string => !!id));
            parentIds = new Set([...filteredParentIds, ...childParentIds]);
        }

        const filteredParents = allParents.filter(parent => parentIds.has(parent.id));

        return {
            childData: filteredChildren,
            parentData: filteredParents
        };
    }

    // Treat a View's filtered data as the widget's base dataset: keep matched rows plus any parent that anchors a matched sub-row.
    filterReportDataAsBaseData(reportData: ReportSiteData[], filter: CompositeFilterDescriptor): ReportSiteData[] {
        const matchedRows = new Set<ReportSiteData>(process(reportData, { filter }).data);
        const anchorParentGridItemIds = new Set<string>();

        matchedRows.forEach((row: ReportSiteData) => {
            if (row.parentId) {
                anchorParentGridItemIds.add(row.parentId);
            }
        });

        const reportSiteData = reportData.filter((row: ReportSiteData) =>
            matchedRows.has(row) || (!row.parentId && !!row.gridItemId && anchorParentGridItemIds.has(row.gridItemId))
        );

        return reportSiteData;
    }

    loadReportData(report: Report | undefined, enableSubRow: boolean, childrenByIndexedParentId: Map<string, ReportSiteData[]>, state: State, grid?: TreeListComponent): ReportGridDataSource {
        this.mapDatesToObjects(report);
        this.generateUniqueIdsForReportData(report);

        if (enableSubRow) {
            this.relateSubRowDataToParentRowsWithUniqueIds(report);
        }

        this.rebuildReportDataCaches(childrenByIndexedParentId, report);

        const stateWithoutPaging = this.prepareStateWithoutPaging(state);
        const sortedData = naturalOrderBy(report?.reportData ? report.reportData : [], stateWithoutPaging.sort || []);
        let dataToDisplay = sortedData.filter(item => !item.parentId);

        const hasFilters = this.hasFiltersInState(stateWithoutPaging);
        const hasSorting = stateWithoutPaging.sort && stateWithoutPaging.sort.length > 0 && stateWithoutPaging.sort.some(s => s.dir !== undefined);

        if (hasFilters) {
            dataToDisplay = this.processFilteredData(sortedData, stateWithoutPaging, enableSubRow, grid, dataToDisplay) ?? [];
        }

        // set sort to empty to prevent Kendo from re-sorting data that was already ordered.
        // When filters were already applied above, clear them here so parent rows retained for
        // matching children are not removed by a second filtering pass.
        const processedState: State = {
            ...stateWithoutPaging,
            filter: hasFilters ? undefined : stateWithoutPaging.filter,
            sort: []
        };

        let gridView: TreeListDataResult;

        if (hasSorting) {
            gridView = process(dataToDisplay ?? [], processedState);
        }

        else {
            gridView = process(dataToDisplay ?? [], hasFilters ? processedState : stateWithoutPaging);
        }

        const reportGridDataSource: ReportGridDataSource = {
            gridView,
            dataSource: gridView.data
        };

        return reportGridDataSource;
    }

    private processFilteredData(sortedData: ReportSiteData[], stateWithoutPaging: State, enableSubRow: boolean, grid?: TreeListComponent, dataToDisplay?: ReportSiteData[]): ReportSiteData[] | undefined {
        const allFiltered = process(sortedData, { filter: stateWithoutPaging.filter }).data;

        if (enableSubRow) {
            const filteredWithSubRows = this.processFilteredDataWithSubRows(allFiltered, dataToDisplay, grid);
            return filteredWithSubRows;
        }
        else {
            const filteredParentIds = new Set(allFiltered.filter(item => !item.parentId).map(item => item.id));
            dataToDisplay = dataToDisplay?.filter(root => filteredParentIds.has(root.id));
        }

        return dataToDisplay;
    }

    private processFilteredDataWithSubRows(allFiltered: ReportSiteData[], dataToDisplay?: ReportSiteData[], grid?: TreeListComponent): ReportSiteData[] | undefined {
        const filteredParentIds = new Set<string>();
        const parentIdsToInclude = new Set<string>();

        allFiltered.forEach((item: ReportSiteData) => {
            if (!item.parentId) {
                filteredParentIds.add(item.id);
                return;
            }

            const parentRow = dataToDisplay?.find((root: ReportSiteData) => root.gridItemId === item.parentId || root.id === item.indexedParentId);
            if (parentRow?.id) {
                parentIdsToInclude.add(parentRow.id);
            }
        });

        const filteredParents = dataToDisplay?.filter((root: ReportSiteData) =>
            filteredParentIds.has(root.id) || parentIdsToInclude.has(root.id)
        );

        const parentRowsToExpand = filteredParents?.filter((root: ReportSiteData) => parentIdsToInclude.has(root.id));
        parentRowsToExpand?.forEach((dataItem: ReportSiteData) => {
            this.expandDataItem(dataItem, grid);
        });

        return filteredParents;
    }

    private mapDatesToObjects(report: Report | undefined): void {
        if (!report) {
            return;
        }

        const dateFields = report.selectedFields?.filter(field =>
            field?.FieldType?.text === FieldTypeName.DateSelect ||
            (field?.FieldType?.text === FieldTypeName.Formula && field?.FieldType?.options?.format === FORM_BUILDER_FORMAT_OPTIONS_CONSTANTS.DATE)
        ) ?? [];

        if (!dateFields.length) {
            return;
        }

        const dateFieldIds = dateFields.map(field => field.serializedId).filter((fieldId): fieldId is string => !!fieldId);

        report.reportData?.forEach((siteData: ReportSiteData) => {
            dateFieldIds.forEach(fieldId => {
                const value = siteData[fieldId];
                if (value === null || value === undefined || value === "" || value instanceof Date) {
                    return;
                }

                siteData[fieldId] = this._dateConversionService.convertToDate(value);
            });
        });
    }

    generateUniqueIdsForReportData(report: Report | undefined): void {
        if (!report) {
            return;
        }

        let uniqueId = 0;
        report.reportData?.forEach((dataItem: ReportSiteData) => {
            dataItem.id = uniqueId.toString();
            uniqueId++;
        });
    }

    // not all rows in reports have gridItemIds and a property to relate to their parent rows are needed for the component to work.
    // Using the Indexed "id" property created in this component
    relateSubRowDataToParentRowsWithUniqueIds(report: Report | undefined): void {
        if (!report?.reportData) {
            return;
        }
        const rowsByGridItemId = new Map<string, ReportSiteData>();
        report.reportData.forEach(row => {
            if (row.gridItemId) {
                rowsByGridItemId.set(row.gridItemId, row);
            }
        });
        report.reportData.forEach(dataItem => {
            if (dataItem.parentId) {
                const parentRow = rowsByGridItemId.get(dataItem.parentId);
                if (parentRow) {
                    dataItem.indexedParentId = parentRow.id;
                }
            }
        });
    }

    private rebuildReportDataCaches(childrenByIndexedParentId: Map<string, ReportSiteData[]>, report: Report | undefined): void {
        childrenByIndexedParentId.clear();

        report?.reportData?.forEach((dataItem: ReportSiteData) => {
            if (dataItem.indexedParentId) {
                this.addDataItemToLookup(childrenByIndexedParentId, dataItem.indexedParentId, dataItem);
            }
        });
    }

    private addDataItemToLookup(lookup: Map<string, ReportSiteData[]>, key: string, dataItem: ReportSiteData): void {
        const existingItems = lookup.get(key);

        if (existingItems) {
            existingItems.push(dataItem);
            return;
        }

        lookup.set(key, [dataItem]);
    }

    private expandDataItem(dataItem: ReportSiteData, grid?: TreeListComponent): void {
        if (dataItem.parentId) {
            return;
        }

        if (grid?.view?.expandState) {
            (grid.view.expandState as unknown as TreeListExpandState).expand(dataItem);
        }
    }

    private prepareStateWithoutPaging(state: State): State {
        return { ...state, take: undefined };
    }

    private prepareStateWithoutSorting(state: State): State {
        return { ...state, sort: undefined };
    }

    private hasFiltersInState(state: State): boolean {
        return (state.filter?.filters?.length ?? 0) > 0;
    }
}
